import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const PhotoGallery = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Real performance photos
  const images = [
    {
      url: '/images/band-stage-green.jpg',
      alt: 'Quetschnmanfred performing live on stage with vibrant green lighting',
      category: 'Live Performance',
    },
    {
      url: '/images/band-stage-blue.jpg',
      alt: 'Band with accordion, saxophone, and drums under blue stage lights',
      category: 'Stage Performance',
    },
    {
      url: '/images/band-stage-red.jpg',
      alt: 'Energetic performance with colorful red and blue lighting effects',
      category: 'Festival',
    },
    {
      url: '/images/band-indoor.jpg',
      alt: 'Traditional Bavarian duo performing indoors with accordion and trumpet',
      category: 'Traditional',
    },
    {
      url: '/images/band-wedding.jpg',
      alt: 'Band performing at a Bavarian wedding celebration with traditional costumes',
      category: 'Wedding',
    },
    {
      url: '/images/accordion-solo.jpg',
      alt: 'Manfred Hahnel playing accordion solo performance',
      category: 'Solo Performance',
    },
    {
      url: '/images/stage-setup.jpg',
      alt: 'Professional stage setup with drum kit and festival banner',
      category: 'Stage Setup',
    },
    {
      url: '/images/manfred-portrait.jpg',
      alt: 'Manfred Hahnel portrait with accordion in traditional vest',
      category: 'Portrait',
    },
    {
      url: '/images/duo-performance.jpg',
      alt: 'Duo performance with guitar and accordion at indoor venue',
      category: 'Duo',
    },
  ];

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage - 1 + images.length) % images.length);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % images.length);
    }
  };

  return (
    <section id="gallery" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold border border-primary/20">
              <Camera className="w-4 h-4" />
              {t.gallery.badge}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-serif font-bold">
              {t.gallery.title}
              <span className="block text-primary mt-2">{t.gallery.titleHighlight}</span>
            </h2>
            
            <p className="text-xl text-foreground/75 max-w-3xl mx-auto leading-relaxed font-medium">
              {t.gallery.description}
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div
                key={index}
                onClick={() => openLightbox(index)}
                className="group relative aspect-square overflow-hidden rounded-2xl cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                {/* Image */}
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white space-y-2">
                    <div className="text-sm font-semibold text-accent">
                      {image.category}
                    </div>
                    <div className="text-base">
                      {image.alt}
                    </div>
                  </div>
                </div>

                {/* Hover Icon */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Camera className="w-5 h-5 text-primary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedImage !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-7xl w-full p-0 bg-black/95 border-none">
          <VisuallyHidden>
            <DialogTitle>
              {selectedImage !== null ? images[selectedImage].alt : 'Photo Gallery'}
            </DialogTitle>
            <DialogDescription>
              {selectedImage !== null ? `Photo ${selectedImage + 1} of ${images.length}: ${images[selectedImage].category}` : 'Photo gallery lightbox'}
            </DialogDescription>
          </VisuallyHidden>
          {selectedImage !== null && (
            <div className="relative">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full"
              >
                <X className="w-6 h-6" />
              </Button>

              {/* Previous Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>

              {/* Next Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>

              {/* Image */}
              <div className="flex items-center justify-center min-h-[80vh] p-12">
                <img
                  src={images[selectedImage].url}
                  alt={images[selectedImage].alt}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              </div>

              {/* Image Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-8 text-white">
                <div className="text-accent text-sm font-semibold mb-2">
                  {images[selectedImage].category}
                </div>
                <div className="text-lg">
                  {images[selectedImage].alt}
                </div>
                <div className="text-sm text-white/60 mt-2">
                  {selectedImage + 1} / {images.length}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PhotoGallery;
